import { useState } from "react";
import { LogProps, LogMethodColors, LogStatusColors } from "@/types/log";
import { ApiDataProps } from "@/pages/Tenants";
import { TypePageActions } from "@/pages/Plans";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Eye, MoreHorizontal, Server, Clock, UserCircle } from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface TableLogsProps {
  logs: LogProps[];
  dataApi: ApiDataProps<LogProps>;
  pageActions: TypePageActions;
}

export function TableLogs({
  logs,
  dataApi,
  pageActions,
}: TableLogsProps) {
  const [selectedLog, setSelectedLog] = useState<LogProps | null>(null);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [viewMode, setViewMode] = useState<"simple" | "advanced">("simple");

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, "dd/MM/yyyy HH:mm:ss", { locale: ptBR });
    } catch (error) {
      return dateString;
    }
  };

  const getMethodColor = (method: string) => {
    return LogMethodColors[method] || LogMethodColors.default;
  };

  const getStatusColor = (status: number) => {
    return LogStatusColors[status] || LogStatusColors.default;
  };

  const showDetails = (log: LogProps) => {
    setSelectedLog(log);
    setOpenDetailDialog(true);
  };

  // Format JSON for display with syntax highlighting
  const formatJson = (data: any) => {
    if (!data) return "Sem dados";
    
    try {
      if (typeof data === 'string') {
        data = JSON.parse(data);
      }
      
      return JSON.stringify(data, null, 2);
    } catch (e) {
      return String(data);
    }
  };

  // Simplifica a descrição da rota para usuários leigos
  const getSimplifiedRoute = (route: string) => {
    if (route.includes("financial-transactions")) return "Transações Financeiras";
    if (route.includes("users")) return "Usuários";
    if (route.includes("tenants")) return "Empresas";
    if (route.includes("units")) return "Unidades";
    if (route.includes("clients")) return "Clientes";
    if (route.includes("products")) return "Produtos";
    if (route.includes("sales")) return "Vendas";
    // Fallback para outras rotas
    return "Operação do Sistema";
  };

  // Descrição amigável do método HTTP
  const getMethodDescription = (method: string) => {
    switch (method) {
      case "GET": return "Consulta";
      case "POST": return "Criação";
      case "PUT": return "Atualização";
      case "DELETE": return "Exclusão";
      default: return method;
    }
  };

  // Descrição amigável do status HTTP
  const getStatusDescription = (status: number) => {
    if (status >= 200 && status < 300) return "Sucesso";
    if (status >= 400 && status < 500) return "Erro de solicitação";
    if (status >= 500) return "Erro do servidor";
    return `Código ${status}`;
  };

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">ID</TableHead>
              <TableHead>Operação</TableHead>
              <TableHead>Recurso</TableHead>
              <TableHead>Mensagem</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Usuário</TableHead>
              <TableHead>Data</TableHead>
              <TableHead className="text-center">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs && logs.length > 0 ? (
              logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="font-medium">{log.id}</TableCell>
                  <TableCell>
                    <Badge className={cn(getMethodColor(log.method))}>
                      {getMethodDescription(log.method)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help">{getSimplifiedRoute(log.route)}</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs">{log.route}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell>{log.message}</TableCell>
                  <TableCell>
                    <Badge className={cn(getStatusColor(log.status_code))}>
                      {getStatusDescription(log.status_code)}
                    </Badge>
                  </TableCell>
                  <TableCell>{log.user?.name}</TableCell>
                  <TableCell>{formatDate(log.created_at)}</TableCell>
                  <TableCell className="text-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => showDetails(log)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center">
                  Nenhum log encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between mb-3">
        {dataApi?.data?.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn(
                    "h-8 bg-blue-600 hover:bg-blue-700",
                    dataApi?.current_page <= 1 &&
                      "pointer-events-none opacity-50"
                  )}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    "size-8 bg-transparent border-2 border-blue-600 text-slate-900 dark:text-white font-bold cursor-auto hover:bg-transparent"
                  )}
                >
                  <span className="pointer-events-none">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black dark:text-white h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn(
                    "h-8 bg-blue-600 hover:bg-blue-700",
                    dataApi?.current_page >= dataApi?.last_page &&
                      "pointer-events-none opacity-50"
                  )}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>

      {/* Details Dialog */}
      <Dialog open={openDetailDialog} onOpenChange={setOpenDetailDialog}>
        <DialogContent className="sm:max-w-[800px]">
          <DialogHeader>
            <DialogTitle>Detalhes do Log</DialogTitle>
            <DialogDescription>
              Informações sobre a operação realizada no sistema
            </DialogDescription>
          </DialogHeader>

          {selectedLog && (
            <div className="space-y-4">
              {/* Modos de visualização */}
              <div className="flex justify-end">
                <div className="border rounded-md overflow-hidden flex">
                  <Button 
                    variant={viewMode === "simple" ? "default" : "outline"} 
                    className="rounded-none"
                    onClick={() => setViewMode("simple")}
                  >
                    Visualização Simples
                  </Button>
                  <Button 
                    variant={viewMode === "advanced" ? "default" : "outline"} 
                    className="rounded-none"
                    onClick={() => setViewMode("advanced")}
                  >
                    Detalhes Técnicos
                  </Button>
                </div>
              </div>

              {viewMode === "simple" ? (
                <>
                  <div className="p-4 border rounded-md space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <h3 className="font-semibold">O que foi feito:</h3>
                        <div className="flex items-center gap-2">
                          <Badge className={cn(getMethodColor(selectedLog.method))}>
                            {getMethodDescription(selectedLog.method)}
                          </Badge>
                          <span>em</span>
                          <span className="font-medium">{getSimplifiedRoute(selectedLog.route)}</span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <h3 className="font-semibold">Quem realizou:</h3>
                        <div className="flex items-center gap-2">
                          <UserCircle className="h-5 w-5 text-blue-500" />
                          <span>{selectedLog.user?.name}</span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <h3 className="font-semibold">Quando:</h3>
                        <div className="flex items-center gap-2">
                          <Clock className="h-5 w-5 text-blue-500" />
                          <span>{formatDate(selectedLog.created_at)}</span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <h3 className="font-semibold">Resultado:</h3>
                        <div className="flex items-center gap-2">
                          <Badge className={cn(getStatusColor(selectedLog.status_code))}>
                            {getStatusDescription(selectedLog.status_code)}
                          </Badge>
                          <span>{selectedLog.message}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Server className="h-4 w-4 text-muted-foreground" />
                        <p className="text-sm font-medium">ID:</p>
                      </div>
                      <p className="text-sm pl-6">{selectedLog.id}</p>
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <UserCircle className="h-4 w-4 text-muted-foreground" />
                        <p className="text-sm font-medium">Usuário:</p>
                      </div>
                      <p className="text-sm pl-6">{selectedLog.user?.name}</p>
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Server className="h-4 w-4 text-muted-foreground" />
                        <p className="text-sm font-medium">Método:</p>
                      </div>
                      <Badge className={cn(getMethodColor(selectedLog.method), "ml-6")}>
                        {selectedLog.method}
                      </Badge>
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Server className="h-4 w-4 text-muted-foreground" />
                        <p className="text-sm font-medium">Status Code:</p>
                      </div>
                      <Badge className={cn(getStatusColor(selectedLog.status_code), "ml-6")}>
                        {selectedLog.status_code}
                      </Badge>
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Server className="h-4 w-4 text-muted-foreground" />
                        <p className="text-sm font-medium">Tipo:</p>
                      </div>
                      <p className="text-sm pl-6">{selectedLog.type}</p>
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <p className="text-sm font-medium">Data:</p>
                      </div>
                      <p className="text-sm pl-6">{formatDate(selectedLog.created_at)}</p>
                    </div>

                    <div className="col-span-2 space-y-1">
                      <div className="flex items-center gap-2">
                        <Server className="h-4 w-4 text-muted-foreground" />
                        <p className="text-sm font-medium">Rota:</p>
                      </div>
                      <p className="text-sm pl-6 break-all">{selectedLog.route}</p>
                    </div>

                    <div className="col-span-2 space-y-1">
                      <div className="flex items-center gap-2">
                        <Server className="h-4 w-4 text-muted-foreground" />
                        <p className="text-sm font-medium">IP:</p>
                      </div>
                      <p className="text-sm pl-6">{selectedLog.ip_address}</p>
                    </div>

                    <div className="col-span-2 space-y-1">
                      <div className="flex items-center gap-2">
                        <Server className="h-4 w-4 text-muted-foreground" />
                        <p className="text-sm font-medium">Mensagem:</p>
                      </div>
                      <p className="text-sm pl-6 break-all">{selectedLog.message}</p>
                    </div>

                    <Tabs defaultValue="payload" className="w-full col-span-2">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="payload">Payload</TabsTrigger>
                        <TabsTrigger value="response">Resposta</TabsTrigger>
                      </TabsList>
                      <TabsContent value="payload" className="p-4 border rounded-md max-h-64 overflow-auto">
                        <pre className="text-xs whitespace-pre-wrap break-all">
                          {formatJson(selectedLog.payload)}
                        </pre>
                      </TabsContent>
                      <TabsContent value="response" className="p-4 border rounded-md max-h-64 overflow-auto">
                        <pre className="text-xs whitespace-pre-wrap break-all">
                          {formatJson(selectedLog.response)}
                        </pre>
                      </TabsContent>
                    </Tabs>
                  </div>
                </>
              )}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setOpenDetailDialog(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
} 